import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Home,
  Clock,
  MapPin,
  Phone,
  Mail,
  AlertCircle,
  CheckCircle2,
  IndianRupee
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import api from "../lib/api";
import { toast } from 'sonner';
import { businessInfo } from '../mock';


const ServiceRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [requestRes, quotesRes] = await Promise.all([
        api.get(`/service-requests/${id}`),
        api.get(`/quotes/request/${id}`)
      ]);
      setRequest(requestRes.data);
      setQuotes(quotesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load request details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to accept this quote? Other quotes will be rejected.')) {
      return;
    }

    try {
      await api.patch(`/quotes/${quoteId}/accept`);
      toast.success('Quote accepted successfully! The service provider will contact you soon.');
      fetchData();
    } catch (error) {
      console.error('Error accepting quote:', error);
      toast.error('Failed to accept quote');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      quoted: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.pending;
  };

  const getUrgencyBadge = (urgency) => {
    const config = {
      urgent: { color: 'bg-red-100 text-red-800', icon: '🔴', label: 'Urgent' },
      normal: { color: 'bg-yellow-100 text-yellow-800', icon: '🟡', label: 'Normal' },
      can_wait: { color: 'bg-green-100 text-green-800', icon: '🟢', label: 'Can Wait' }
    };
    return config[urgency] || config.normal;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!request) return null;

  const urgencyConfig = getUrgencyBadge(request.urgency);
  const acceptedQuote = quotes.find(q => q.status === 'accepted');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center space-x-2">
              <Home className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{businessInfo.name}</h1>
                <p className="text-xs text-gray-600">Service Request Status</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Request Details */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-2xl mb-2">{request.problemTitle}</CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={getStatusColor(request.status)}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </Badge>
                    <Badge className={urgencyConfig.color}>
                      {urgencyConfig.icon} {urgencyConfig.label}
                    </Badge>
                    <Badge variant="outline">{request.category}</Badge>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Problem Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{request.problemDescription}</p>
              </div>

              {request.images && request.images.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Problem Images</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {request.images.map((img, index) => (
                      <div key={index} className="rounded-lg overflow-hidden border">
                        <img src={img} alt={`Problem ${index + 1}`} className="w-full h-32 object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-3 gap-4 pt-4 border-t">
                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="font-medium">{request.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Phone className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-gray-500">Contact</p>
                    <p className="font-medium">{request.customerPhone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-gray-500">Submitted</p>
                    <p className="font-medium">{new Date(request.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quotes Section */}
          <Card>
            <CardHeader>
              <CardTitle>
                Quotes Received ({quotes.length})
                {quotes.length === 0 && <span className="text-sm font-normal text-gray-500 ml-2">- Waiting for providers to respond</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {quotes.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">No quotes received yet</p>
                  <p className="text-sm text-gray-400">Service providers will send their quotes soon</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {quotes.map((quote) => (
                    <Card key={quote.id} className={`${quote.status === 'accepted' ? 'border-2 border-green-500' : 'hover:shadow-md'} transition-all`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">{quote.providerName}</h3>
                              {quote.status === 'accepted' && (
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Accepted
                                </Badge>
                              )}
                              {quote.status === 'rejected' && (
                                <Badge className="bg-gray-100 text-gray-600">Rejected</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <Phone className="h-4 w-4" />
                                <a href={`tel:${quote.providerPhone}`} className="hover:text-blue-600">
                                  {quote.providerPhone}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="bg-blue-50 rounded-lg p-4">
                            <div className="flex items-center gap-2 text-blue-700 mb-1">
                              <IndianRupee className="h-5 w-5" />
                              <span className="text-sm font-medium">Estimated Cost</span>
                            </div>
                            <p className="text-2xl font-bold text-blue-900">{quote.estimatedCost}</p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-4">
                            <div className="flex items-center gap-2 text-green-700 mb-1">
                              <Clock className="h-5 w-5" />
                              <span className="text-sm font-medium">Estimated Time</span>
                            </div>
                            <p className="text-2xl font-bold text-green-900">{quote.estimatedTime}</p>
                          </div>
                        </div>

                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Quote Details:</h4>
                          <p className="text-gray-700">{quote.description}</p>
                        </div>

                        {quote.status === 'pending' && !acceptedQuote && (
                          <Button 
                            onClick={() => handleAcceptQuote(quote.id)} 
                            className="w-full bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Accept This Quote
                          </Button>
                        )}

                        {quote.status === 'accepted' && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                            <div className="text-sm text-green-800">
                              <p className="font-semibold mb-1">Quote Accepted!</p>
                              <p>The service provider will contact you at {request.customerPhone} to schedule the work.</p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {quotes.length > 0 && !acceptedQuote && (
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Compare & Choose</p>
                    <p>Review all quotes carefully. Compare cost, time, and details before accepting. Once accepted, the provider will contact you directly.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ServiceRequestDetail;
